// The oracle-labelled face of the engine-neutral introspection query layer
// (SLICES.md § S7 items 2 + 4 + 7 + 8). Both Oracle fetchers —
// `oracleSchemaIntrospection.ts` and `oracleRoutineIntrospection.ts` — page
// through here, which is also why it is its own module: the schema fetcher
// imports the routine fetcher, so a shared helper living in either of them
// would close an import cycle.
//
// Oracle's divergence from the other four engines lives here: `OFFSET … ROWS
// FETCH NEXT … ROWS ONLY` is the SQL:2008 row-limiting clause Oracle only
// added in 12.1, so every fetch is told whether it may page at all.

import type { IntrospectionRow, PagedIntrospectionOutcome } from './introspectionPaging';
import {
  fetchIntrospectionProbeRows,
  fetchPagedArtefact,
  introspectionFailureError,
  type PagedFetchOptions,
} from './introspectionQuery';
import { runIntrospectionQuery } from './introspectionSemaphore';
import {
  reportIntrospectionFailure,
  reportIntrospectionTruncation,
  type TruncationArtefact,
} from './introspectionTruncation';

export type { PagedFetchOptions };

/** One page's worth of aggregated items, handed to phase 2's merge. */
export type OraclePageSink<TItem> = (items: readonly TItem[]) => void | Promise<void>;

/** A relation-scoped page item — the table it belongs to plus the payload. */
export type OracleRelationScoped<TItem> = { readonly table: string; readonly item: TItem };

/**
 * Per-request cap for the PRE-12c fallback path (see
 * {@link oracleSupportsRowLimiting}). `OFFSET … ROWS FETCH NEXT … ROWS ONLY`
 * is the SQL:2008 row-limiting clause Oracle only added in 12.1 — on an older
 * server it raises ORA-00933, so those releases keep the pre-S7 shape: ONE
 * capped query per artefact, with a truncation report when the cap clips it.
 * Must stay ≤ the query cache's windowing threshold (50,000); see the "Cap
 * constraint" note in `introspectionTruncation.ts`.
 */
const ORACLE_LEGACY_MAX_ROWS = 50_000;

/**
 * Does this server understand `OFFSET … ROWS FETCH NEXT … ROWS ONLY`?
 * True for 12.1 and up. An unparseable/absent version answers `false` — the
 * capped single-query fallback is correct on every release, so "don't know"
 * has to mean "don't page".
 */
export function oracleSupportsRowLimiting(serverVersion: string | undefined): boolean {
  const major = Number.parseInt(String(serverVersion ?? '').trim(), 10);
  return Number.isFinite(major) && major >= 12;
}

/** How a fetch reaches the server: paged (12c+) or one capped query (pre-12c). */
export type OraclePagingMode = PagedFetchOptions & {
  /** From {@link oracleSupportsRowLimiting}. Defaults to `true`. */
  readonly supportsRowLimiting?: boolean;
};

export async function probeRows(
  connectionId: string,
  schema: string,
  label: string,
  sql: string,
): Promise<readonly IntrospectionRow[]> {
  return fetchIntrospectionProbeRows({
    engine: 'oracle',
    connectionId,
    database: schema,
    label,
    sql,
  });
}

/**
 * Page one Oracle artefact — or, on a pre-12c server, issue the single capped
 * query the row-limiting clause is unavailable for and report the clip.
 * `sqlForPage('')` is the un-paged form of the same statement, which is why
 * every builder appends the clause rather than interpolating it mid-statement.
 *
 * Same contract as `fetchPagedArtefact` on both paths: a failed query REJECTS
 * (release review 1.5.0, F3), a successful empty result still reaches the
 * sink once (F5), and the resolved outcome says whether the whole artefact
 * was read.
 */
export async function pageOracleArtefact<TItem>(args: {
  connectionId: string;
  schema: string;
  label: string;
  artefact: TruncationArtefact;
  totalBudget: number;
  sqlForPage: (pageClause: string) => string;
  aggregate: (rows: readonly IntrospectionRow[]) => TItem[];
  sink: OraclePageSink<TItem>;
  groupKeyOf?: (row: IntrospectionRow) => string;
  mode?: OraclePagingMode;
}): Promise<PagedIntrospectionOutcome> {
  const { connectionId, schema, mode } = args;
  if (mode?.supportsRowLimiting === false) {
    const aborted: PagedIntrospectionOutcome = {
      failed: false, capHit: false, aborted: true, rowsRead: 0, pages: 0,
    };
    if (mode.shouldAbort?.()) return aborted;
    const legacy = await runLegacyOracleQuery({
      connectionId,
      schema,
      label: args.label,
      sql: args.sqlForPage(''),
      artefact: args.artefact,
      shouldAbort: mode.shouldAbort,
    });
    if (legacy === undefined) return aborted;
    if (mode.shouldAbort?.()) return aborted;
    await args.sink(args.aggregate(legacy.rows));
    return { failed: false, capHit: legacy.truncated, aborted: false, rowsRead: legacy.rows.length, pages: 1 };
  }
  return fetchPagedArtefact<TItem>({
    engine: 'oracle',
    dialect: 'offset-fetch',
    connectionId,
    database: schema,
    label: args.label,
    artefact: args.artefact,
    totalBudget: args.totalBudget,
    sqlForPage: args.sqlForPage,
    aggregate: args.aggregate,
    sink: args.sink,
    groupKeyOf: args.groupKeyOf,
    shouldAbort: mode?.shouldAbort,
  });
}

/**
 * Pre-12c fallback: one query, capped at `ORACLE_LEGACY_MAX_ROWS`, reporting
 * the clip against the artefact that lost rows. Goes through the same
 * per-connection introspection lane as a page so it cannot starve a user query
 * either.
 */
async function runLegacyOracleQuery(args: {
  connectionId: string;
  schema: string;
  label: string;
  sql: string;
  artefact: TruncationArtefact;
  shouldAbort?: () => boolean;
}): Promise<{ rows: readonly IntrospectionRow[]; truncated: boolean } | undefined> {
  const executeQueryV2 = window.electronAPI?.executeQueryV2;
  if (!executeQueryV2) return { rows: [], truncated: false };
  return runIntrospectionQuery(args.connectionId, async () => {
    const result = await executeQueryV2({
      connectionId: args.connectionId,
      database: args.schema,
      sql: args.sql,
      executionId: `oracle-ls-${args.label}-${args.connectionId}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      options: { maxRows: ORACLE_LEGACY_MAX_ROWS },
    });
    // Superseded while in flight: report nothing, the current load owns the
    // scope's record now (round 3 N2).
    if (args.shouldAbort?.()) return undefined;
    if (!result.success) {
      reportIntrospectionFailure({
        connectionId: args.connectionId,
        database: args.schema,
        artefact: args.artefact,
        message: result.error?.message,
      });
      throw introspectionFailureError(args.artefact, result.error?.message);
    }
    const truncated = result.stats?.truncated === true;
    if (truncated) {
      reportIntrospectionTruncation({
        connectionId: args.connectionId,
        database: args.schema,
        engine: 'oracle',
        cap: ORACLE_LEGACY_MAX_ROWS,
        artefact: args.artefact,
      });
    }
    return { rows: (result.resultSets?.[0]?.rows ?? []) as readonly IntrospectionRow[], truncated };
  });
}

